import { create } from 'zustand'
import type {
  MultiSheetImportProposal,
  MultiSheetProgress,
  ApprovedDependency,
  EngineImportPlan,
  EngineMultiSheetResult,
} from '../types/import'

type ImportStep = 'idle' | 'analysis' | 'dependencies' | 'confirm' | 'progress' | 'done' | 'error'

interface ImportState {
  currentProposal: MultiSheetImportProposal | null
  importProgress: MultiSheetProgress | null
  approvedDependencies: ApprovedDependency[]
  step: ImportStep
  /** Terminal error message for the synchronous import execute path. */
  importError: string | null
  /**
   * fileId of the uploaded workbook. Carried so the deterministic execute
   * call (POST /api/v1/import/execute) has the fileId the engine requires —
   * the analyze response includes it; the store mirrors it for the confirm
   * step. See ADR: remove LLM from the import driver path.
   */
  fileId: string | null
  /** Authoritative engine plan (sheetPlans + importOrder) used to execute. */
  enginePlan: EngineImportPlan | null
  /** Synchronous result from execute — drives the final report. */
  executeResult: EngineMultiSheetResult | null
  /**
   * i18n KEY (never resolved text) for the last deterministic execute()
   * failure that returned the flow to the 'confirm' step — e.g.
   * `'import.executeFailed'`. Consumers apply `t()`; never render this
   * verbatim. `null` when there is nothing to show.
   *
   * P0-7 Parte A: the legacy LLM-streamed multi-sheet flow
   * (`useMessageActions.handleMultiSheetExecute`) re-enters 'confirm' on
   * execute failure and sets this so `ImportConfirmCard` can render a
   * `role="alert"` banner. The deterministic REST flow
   * (`ImportFlowCards.execute`) never re-enters 'confirm' on failure — it
   * emits an immutable `import_report` message instead — so it never writes
   * this field; it only fires the toast (see `notifyImportExecuteError`).
   *
   * Cleared by `clearImport()` and by `setStep()` whenever the target step
   * is not `'confirm'` (the only step that renders the banner) — a retry or
   * cancel always wipes stale error text.
   */
  executeError: string | null

  setProposal: (p: MultiSheetImportProposal | null) => void
  updateImportProgress: (p: MultiSheetProgress) => void
  setApprovedDependencies: (deps: ApprovedDependency[]) => void
  setStep: (step: ImportStep) => void
  setFileId: (fileId: string | null) => void
  setEnginePlan: (plan: EngineImportPlan | null) => void
  setExecuteResult: (result: EngineMultiSheetResult | null) => void
  setImportError: (message: string | null) => void
  /** Store the i18n key for the last execute() failure (or `null` to clear it). */
  setExecuteError: (key: string | null) => void
  clearImport: () => void
}

export const useImportStore = create<ImportState>((set) => ({
  currentProposal: null,
  importProgress: null,
  approvedDependencies: [],
  step: 'idle',
  importError: null,
  fileId: null,
  enginePlan: null,
  executeResult: null,
  executeError: null,

  setProposal: (p) => set({ currentProposal: p }),
  updateImportProgress: (p) => set({ importProgress: p }),
  setApprovedDependencies: (deps) => set({ approvedDependencies: deps }),
  setStep: (step) =>
    set((state) => ({ step, executeError: step === 'confirm' ? state.executeError : null })),
  setFileId: (fileId) => set({ fileId }),
  setEnginePlan: (plan) => set({ enginePlan: plan }),
  setExecuteResult: (result) => set({ executeResult: result }),
  setImportError: (message) => set({ importError: message }),
  setExecuteError: (key) => set({ executeError: key }),
  clearImport: () =>
    set({
      currentProposal: null,
      importProgress: null,
      approvedDependencies: [],
      step: 'idle',
      importError: null,
      fileId: null,
      enginePlan: null,
      executeResult: null,
      executeError: null,
    }),
}))
